/**
 * Aggiorna la descrizione di un registro già aperto su RENTRI (PUT anagrafiche).
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { ApiError, endpoints } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function rentriDetail(body: unknown): string {
  if (!body || typeof body !== "object") return "";
  const o = body as Record<string, unknown>;
  const detail = o.detail ?? o.title ?? o.Detail ?? o.Title;
  if (typeof detail === "string") return detail;
  return JSON.stringify(body).slice(0, 300);
}

export function ModificaDescrizioneRegistroDialog({
  registroId,
  descrizioneAttuale,
  registriListQueryKey,
  chiuso = false,
  open,
  onOpenChange,
}: {
  registroId: string;
  descrizioneAttuale: string;
  /** Query key lista registri del sito (per refresh UI dopo PUT). */
  registriListQueryKey?: readonly unknown[];
  chiuso?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [descrizione, setDescrizione] = useState("");

  useEffect(() => {
    if (open) {
      setDescrizione(descrizioneAttuale === "—" ? "" : descrizioneAttuale);
    }
  }, [open, descrizioneAttuale]);

  const saveMut = useMutation({
    mutationFn: () => {
      const trimmed = descrizione.trim();
      return endpoints.registri.modifica(registroId, {
        descrizione: trimmed.length > 0 ? trimmed : null,
      });
    },
    onSuccess: async (data) => {
      const status = data.rentri_status;
      if (status >= 200 && status < 300) {
        const trimmed = descrizione.trim();
        toast.success("Descrizione aggiornata su RENTRI", {
          description: trimmed || "Descrizione rimossa",
        });
        const listKey = registriListQueryKey ?? ["registri"];
        await qc.invalidateQueries({ queryKey: listKey });
        await qc.refetchQueries({ queryKey: listKey });
        await qc.invalidateQueries({ queryKey: ["ctx-registri"] });
        onOpenChange(false);
      } else {
        toast.error("RENTRI ha rifiutato la modifica", {
          description: `HTTP ${status} — ${rentriDetail(data.body)}`,
        });
      }
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const status = err.status;
        const detail = rentriDetail(err.body);
        toast.error(
          status >= 500
            ? "Errore server durante l'aggiornamento"
            : `RENTRI ha rifiutato la modifica (HTTP ${status})`,
          { description: detail || err.message },
        );
        return;
      }
      toast.error("Errore aggiornamento descrizione", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Modifica descrizione registro
          </DialogTitle>
          <DialogDescription className="text-xs">
            Registro{" "}
            <span className="font-mono">{registroId}</span>
            {chiuso ? (
              <span className="mt-1 block text-amber-600 dark:text-amber-400">
                Il registro risulta chiuso: la modifica potrebbe essere rifiutata da RENTRI.
              </span>
            ) : (
              <span className="mt-1 block">
                La modifica viene inviata a RENTRI (max 250 caratteri). Le attività del registro non
                sono modificabili.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="registro-desc">
            Descrizione
          </label>
          <textarea
            id="registro-desc"
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            maxLength={250}
            rows={4}
            disabled={saveMut.isPending}
            placeholder="Es. Registro produzione cantiere via Roma 12"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
          <p className="text-right text-xs text-muted-foreground">{descrizione.length}/250</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saveMut.isPending}>
            Annulla
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? "Salvataggio…" : "Salva su RENTRI"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
